/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'sharedUi/Button';
import { valueSetsService } from '../../services/value-sets.service';
import type { ParameterDefinition } from '../../services/notification-masters.service';

/**
 * The payload variables a notification declares.
 *
 * Declaring them is what lets saving check two things it otherwise could not:
 * that every template placeholder resolves to something, and that every
 * required parameter has a source. A template referencing an undeclared name is
 * refused rather than rendering blank.
 */
interface ParameterEditorProps {
  value: ParameterDefinition[];
  onChange: (parameters: ParameterDefinition[]) => void;
  testId?: string;
}

const BLANK: ParameterDefinition = { name: '', dataType: 'string', isRequired: true };

const ParameterEditor = ({ value, onChange, testId = 'ParameterEditor' }: ParameterEditorProps) => {
  const { data: typeVS } = useQuery({
    queryKey: ['vs', 'parameterDataType'],
    queryFn: () => valueSetsService.getByKey('parameterDataType', 'en'),
    staleTime: 3e5,
  });
  const options = (typeVS?.items || []).map((i: any) => ({ value: i.code, label: i.label }));

  const update = (index: number, next: ParameterDefinition) =>
    onChange(value.map((p, i) => (i === index ? next : p)));

  const duplicates = new Set(
    value.map((p) => p.name).filter((n, i, all) => n && all.indexOf(n) !== i),
  );

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Parameters</label>
        <span className="block text-xs text-gray-400">
          What the trigger payload supplies. Templates reference these by name as
          <span className="font-mono"> {'{{name}}'}</span>.
        </span>
      </div>

      {value.length === 0 && (
        <p className="text-xs text-gray-400">None — templates can only use static text.</p>
      )}

      {value.map((parameter, index) => (
        <div key={index} className="flex items-center gap-2 flex-wrap">
          <InputText
            value={parameter.name || ''}
            placeholder="name"
            className={`w-48 font-mono text-sm${duplicates.has(parameter.name) ? ' p-invalid' : ''}`}
            data-testid={`${testId}.Name.${index}`}
            onChange={(e) => update(index, { ...parameter, name: e.target.value })}
          />
          <Dropdown
            value={parameter.dataType || 'string'}
            options={options}
            optionLabel="label"
            optionValue="value"
            className="w-36"
            data-testid={`${testId}.Type.${index}`}
            onChange={(e) => update(index, { ...parameter, dataType: e.value })}
          />
          <div className="flex items-center gap-1.5">
            <Checkbox
              inputId={`${testId}-required-${index}`}
              checked={parameter.isRequired !== false}
              onChange={(e) => update(index, { ...parameter, isRequired: !!e.checked })}
            />
            <label htmlFor={`${testId}-required-${index}`} className="text-xs text-gray-700 dark:text-gray-300">
              Required
            </label>
          </div>
          <InputText
            value={(parameter.sample as string) || ''}
            placeholder="sample value"
            className="w-40 text-sm"
            data-testid={`${testId}.Sample.${index}`}
            onChange={(e) => update(index, { ...parameter, sample: e.target.value })}
          />
          <InputText
            value={parameter.description || ''}
            placeholder="description"
            className="flex-1 text-sm"
            data-testid={`${testId}.Description.${index}`}
            onChange={(e) => update(index, { ...parameter, description: e.target.value })}
          />
          <button aria-label="Remove parameter"
            type="button"
            title="Remove parameter"
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => onChange(value.filter((_, i) => i !== index))}
          >
            <i className="pi pi-trash text-red-600 dark:text-red-400" />
          </button>
        </div>
      ))}

      {duplicates.size > 0 && (
        <small className="text-red-500">
          Declared more than once: {[...duplicates].join(', ')} — a template referencing the name
          could mean either.
        </small>
      )}

      <div>
        <Button type="button" label="Add parameter" icon="pi pi-plus" size="small" severity="secondary" outlined
          onClick={() => onChange([...value, { ...BLANK }])} data-testid={`${testId}.Add`} />
      </div>
    </div>
  );
};

export default ParameterEditor;
